import React, { useCallback, useState, useEffect, useRef } from "react";
import { get, isNil } from "lodash";
import Badge from "antd/lib/badge";
import Input from "antd/lib/input";
import Button from "antd/lib/button";
import Modal from "antd/lib/modal";
import { Row, Col } from "antd/lib/grid";

import notification from "@/services/notification";
import SmartContractService from '@/services/smart-contract';

import DynamicForm from "@/components/dynamic-form/DynamicForm";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import { useUniqueId } from "@/lib/hooks/useUniqueId";
import { useTheme } from "@/components/ThemeProvider";
import PropTypes from 'prop-types';

import "./SmartContractDialog.less";

function SmartContractDialog({ smartContract, dialog, readOnly }) {
  const formValuesRef = useRef({});
  const { isDarkMode } = useTheme();
  const [localSmartContract, setLocalSmartContract] = useState(smartContract);
  const [abiInput, setAbiInput] = useState(smartContract?.abi || '');
  const [parsedAbi, setParsedAbi] = useState(null);
  const [abiError, setAbiError] = useState(null);

  useEffect(() => {
    if (smartContract?.abi) {
      parseABI(smartContract.abi);
    }
  }, [localSmartContract]);

  const parseABI = useCallback((abiInput) => {
    if (!abiInput || !abiInput.trim()) {
      setAbiError(null);
      setParsedAbi(null);
      return;
    }

    try {
      const parsed = JSON.parse(abiInput);
      if (!Array.isArray(parsed)) {
        throw new Error("ABI must be an array");
      }
      
      setParsedAbi(parsed);
      setAbiError(null);
    } catch (error) {
      console.error("ABI parsing error:", error);
      setAbiError("Invalid ABI: " + error.message);
      setParsedAbi(null);
    }
  }, []);

  useEffect(() => {
    parseABI(abiInput);
  }, [abiInput, parseABI]);

  const handleSubmit = useCallback(
    (values) => {
      const updatedValues = {
        ...localSmartContract,
        ...values,
      };

      // Ensure ABI is always a string, even if empty
      if (!updatedValues.abi) {
        updatedValues.abi = '[]';
      }

      console.log('Submitting updated values:', updatedValues);

      dialog.close(updatedValues);
    },
    [localSmartContract, dialog]
  );

  const refreshData = useCallback(async () => {
    if (localSmartContract?.id) {
      try {
        const updatedContract = await SmartContractService.get({ id: localSmartContract.id });
        setLocalSmartContract(updatedContract);
      } catch (error) {
        console.error("Error refreshing data:", error);
        notification.error("Failed to refresh data");
      }
    }
  }, [localSmartContract]);

  const handleFormChange = useCallback((changedValues, allValues) => {
    formValuesRef.current = { ...formValuesRef.current, ...allValues };
    
    // If ABI changed, update abiInput state
    if ('abi' in changedValues) {
      setAbiInput(changedValues.abi);
    }
  }, []);

  const formFields = [
    { name: "name", title: "Name", type: "text" },
    { name: "project_name", title: "Project Name", type: "text" },
    { name: "deploy_block", title: "Deployment Block", type: "text" },
    { name: "chain_id", title: "Chain", type: "text" },
    { name: "address", title: "Address", type: "text" },
    { name: "implementation_address", title: "Implementation Address", type: "text" },
    { name: "decimals", title: "Decimals", type: "number" },
    { 
      name: "abi", 
      title: "ABI", 
      type: "ace", 
      required: true,
      mode: "json",
      placeholder: "Paste your ABI here. It should be a valid JSON array.",
      help: abiError ? <span style={{ color: 'red' }}>{abiError}</span> : "Enter a valid JSON ABI array",
      value: abiInput,
      onChange: (value) => {
        console.log('ABI changed:', value);
        setAbiInput(value);
      },
    },
  ].map(field => ({ 
    ...field, 
    readOnly, 
    initialValue: get(localSmartContract, field.name, ""),
  }));

  const smartContractsFormId = useUniqueId("smartContractForm");

  return (
    <div className={`smart-contract-dialog ${isDarkMode ? 'dark-mode' : ''}`}>
      <Modal
        {...dialog.props}
        title={localSmartContract?.id ? localSmartContract.name : "Create Smart Contract"}
        footer={[
          <Button key="cancel" {...dialog.props.cancelButtonProps} onClick={dialog.dismiss}>
            {readOnly ? "Close" : "Cancel"}
          </Button>,
          !readOnly && (
            <Button
              key="submit"
              {...dialog.props.okButtonProps}
              disabled={readOnly || dialog.props.okButtonProps.disabled}
              htmlType="submit"
              type="primary"
              form={smartContractsFormId}
              data-test="SaveSmartContractButton">
              {localSmartContract?.id ? "Save" : "Create"}
            </Button>
          ),
        ]}
        wrapProps={{
          "data-test": "SmartContractDialog",
        }}
        className={`smart-contract-dialog ${isDarkMode ? 'dark-mode' : ''}`}
        width={1000}
      >
        <Row gutter={16}>
          <Col span={24}>
            <DynamicForm
              id={smartContractsFormId}
              fields={formFields}
              onSubmit={handleSubmit}
              onChange={handleFormChange}
              hideSubmitButton
              feedbackIcons
            />
          </Col>
        </Row>
        {parsedAbi && (
          <Row gutter={16}>
            <Col span={24}>
              <h3>Parsed ABI</h3>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '8px' }}>
                {parsedAbi.map((item, index) => (
                  <div key={index}>
                    <strong>{item.type}</strong>: {item.name}
                    {item.inputs && (
                      <ul>
                        {item.inputs.map((input, idx) => (
                          <li key={idx}>{input.name}: {input.type}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
}

SmartContractDialog.propTypes = {
  dialog: DialogPropType.isRequired,
  smartContract: PropTypes.object,
  readOnly: PropTypes.bool,
};

SmartContractDialog.defaultProps = {
  smartContract: null,
  readOnly: false,
};

export default wrapDialog(SmartContractDialog);